import {
  useConversations,
  useSpaceConversations,
  useSpaceConversationsSummary,
  useSpaceUnreadConversationIds,
} from "@app/hooks/conversations";
import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import type { WorkspaceType } from "@app/types/user";
import { useCallback, useState } from "react";

interface useMarkAllConversationsAsReadParams {
  owner: WorkspaceType;
  spaceId?: string;
}

export function useMarkAllConversationsAsRead({
  owner,
  spaceId,
}: useMarkAllConversationsAsReadParams) {
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const sendNotification = useSendNotification();
  const { mutateConversations } = useConversations({ workspaceId: owner.sId });

  const { mutate: mutateSpaceSummary } = useSpaceConversationsSummary({
    workspaceId: owner.sId,
    options: { disabled: true },
  });

  const { mutateConversations: mutateSpaceConversations } =
    useSpaceConversations({
      workspaceId: owner.sId,
      spaceId: spaceId ?? null,
    });

  const { mutateUnreadConversationIds } = useSpaceUnreadConversationIds({
    workspaceId: owner.sId,
    spaceId: spaceId ?? null,
  });

  const markAllAsRead = useCallback(
    async (conversationIds: string[]) => {
      if (conversationIds.length === 0) {
        return;
      }

      setIsMarkingAllAsRead(true);

      const total = conversationIds.length;

      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/assistant/conversations/bulk-actions`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "mark_as_read",
              conversationIds,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to mark conversations as read");
        }

        const { success } = await response.json();

        if (!success) {
          throw new Error("Failed to mark conversations as read");
        }

        void mutateConversations();
        void mutateSpaceSummary();
        void mutateSpaceConversations();
        void mutateUnreadConversationIds();

        sendNotification({
          type: "success",
          title: "All conversations marked as read",
          description: `${total} conversation${total > 1 ? "s" : ""} marked as read.`,
        });
      } catch {
        sendNotification({
          type: "error",
          title: "Failed to mark conversations as read",
          description: `Could not mark the ${total > 1 ? "conversations" : "conversation"} as read.`,
        });
      } finally {
        setIsMarkingAllAsRead(false);
      }
    },
    [
      owner.sId,
      mutateConversations,
      mutateSpaceSummary,
      mutateSpaceConversations,
      sendNotification,
      mutateUnreadConversationIds,
    ]
  );

  return {
    markAllAsRead,
    isMarkingAllAsRead,
  };
}
